import React, { useState, useEffect } from "react";
import { X, Search, CheckCircle2, Loader2, UserPlus } from "lucide-react";
import { apiClient } from "../../../lib/api";

// ----------------------------------------------------------------------
// Interfaces
// ----------------------------------------------------------------------
interface AssignAdminRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface UserSearchResult {
  userId: number;
  name: string;
  lastName: string;
  email: string;
  photoLink?: string;
}

const AssignAdminRoleModal: React.FC<AssignAdminRoleModalProps> = ({ isOpen, onClose, onSuccess }) => {
  // Estados
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  
  const [isSearching, setIsSearching] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Limpiar el modal cada vez que se abre/cierra
  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setSearchResults([]);
      setSelectedUser(null);
      setErrorMsg(null);
    }
  }, [isOpen]);

  // Manejador de búsqueda
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setIsSearching(true);
    setErrorMsg(null);
    setSelectedUser(null);

    try {
      // Parámetros obligatorios y de búsqueda
      const params = new URLSearchParams({
        pageNumber: "1",
        pageSize: "10",
        orderBy: "recent",
        search: searchTerm.trim()
      });

      const endpoint = `GetUserCards?${params.toString()}`;
      const response = await apiClient.get<any>(endpoint);
      
      // Extrae la data
      const rawData = response.data?.data || response.data || [];

      // Mapea los datos a la interfaz del modal
      const mappedResults: UserSearchResult[] = rawData.map((item: any) => ({
        userId: item.userId,
        name: item.name ? item.name.trim() : "",
        lastName: item.lastName ? item.lastName.trim() : "",
        email: item.email || "Sin correo",
        photoLink: item.photoLink || ""
      }));

      setSearchResults(mappedResults);

      if (mappedResults.length === 0) {
        setErrorMsg("No se encontraron usuarios con ese término.");
      }
    } catch (error) {
      console.error("Error buscando usuarios:", error);
      setErrorMsg("Ocurrió un error al buscar usuarios.");
    } finally {
      setIsSearching(false);
    }
  };

  // Manejador para asignar el rol
  const handleAssignRole = async () => {
    if (!selectedUser) return;

    setIsAssigning(true);
    setErrorMsg(null);

    try {
      // Endpoint
      await apiClient.put(`User/EnableAdminRole?userId=${selectedUser.userId}`);
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error asignando rol:", error);
      setErrorMsg("No se pudo asignar el rol al usuario. Intenta nuevamente.");
    } finally {
      setIsAssigning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div 
        className="bg-white w-full max-w-lg rounded-xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <div className="flex items-center gap-2 text-gray-900">
            <UserPlus size={20} className="text-[#4f6df5]" />
            <h2 className="text-[16px] font-bold m-0">Asignar rol de Administrador</h2>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 transition-colors p-1 rounded-md hover:bg-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 flex flex-col gap-4">
          <p className="text-sm text-gray-600">
            Busca un usuario existente en la plataforma por su nombre o correo electrónico para otorgarle permisos de administrador.
          </p>

          {/* Formulario de búsqueda */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Nombre o correo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-300 text-gray-900 rounded-lg text-sm focus:ring-2 focus:ring-[#4f6df5] focus:border-transparent outline-none transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={isSearching || !searchTerm.trim()}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-w-[80px]"
            >
              {isSearching ? <Loader2 size={16} className="animate-spin" /> : "Buscar"}
            </button>
          </form>

          {/* Mensaje de error */}
          {errorMsg && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-100">
              {errorMsg}
            </div>
          )}

          {/* Resultados de búsqueda */}
          {searchResults.length > 0 && (
            <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden flex flex-col max-h-[200px]">
              <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">
                Resultados ({searchResults.length})
              </div>
              <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">
                {searchResults.map((user) => (
                  <div
                    key={user.userId}
                    onClick={() => setSelectedUser(user)}
                    className={`flex items-center justify-between p-3 cursor-pointer border-b border-gray-100 last:border-0 transition-colors ${
                      selectedUser?.userId === user.userId 
                        ? "bg-[#4f6df5]/10" 
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {user.photoLink ? (
                        <img src={user.photoLink} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-bold">
                          {user.name.charAt(0)}{user.lastName?.charAt(0)}
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {user.name} {user.lastName}
                        </span>
                        <span className="text-xs text-gray-500">{user.email}</span>
                      </div>
                    </div>
                    {selectedUser?.userId === user.userId && (
                      <CheckCircle2 size={18} className="text-[#4f6df5]" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={isAssigning}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm"
          >
            Cancelar
          </button>
          <button
            onClick={handleAssignRole}
            disabled={!selectedUser || isAssigning}
            className="px-4 py-2 bg-[#4f6df5] text-white rounded-lg text-sm font-semibold hover:bg-[#3b5bdb] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm flex items-center gap-2"
          >
            {isAssigning && <Loader2 size={16} className="animate-spin" />}
            Asignar como Administrador
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignAdminRoleModal;